import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, BottomNavigation, BottomNavigationAction, Paper, Divider, useMediaQuery, useTheme, Collapse, Avatar,  } from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as InvoiceIcon,
  Add as AddIcon,
  List as ListIcon,
  People as PeopleIcon,
  BarChart as StatsIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Store as StoreIcon,
  ShoppingCart as OrderIcon,
  ContentCut as TailorIcon,
  Build as InstallerIcon,
  Group as SalespersonIcon,
} from '@mui/icons-material';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { loggedInState, userInfoState } from '../recoil/atoms';

const drawerWidth = 260;

const themeColors = {
  dark: '#1a1a2e',
  darker: '#16162a',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
  goldDark: '#b8960c',
  text: '#ffffff',
  textMuted: '#a0a0a0',
};

const toPersianDate = () => {
  const now = new Date();
  const options = { 
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  };
  return now.toLocaleDateString('fa-IR', options);
};

const toPersianTime = () => {
  const now = new Date();
  const options = { 
    timeZone: 'Asia/Tehran',
    hour: '2-digit',
    minute: '2-digit',
  };
  return now.toLocaleTimeString('fa-IR', options);
};

const ShopLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(true);
  const [currentTime, setCurrentTime] = useState(toPersianTime());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const setLoggedIn = useSetRecoilState(loggedInState);
  const setUserInfo = useSetRecoilState(userInfoState);
  const userInfo = useRecoilValue(userInfoState);
  
  // چک کردن نقش کاربر - case insensitive
  const userRole = userInfo?.role?.toLowerCase() || '';
  const isShopOwner = userRole === 'shop';
  const isSalesperson = userRole === 'salesperson';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(toPersianTime());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user');
    setLoggedIn(false);
    setUserInfo(null);
    navigate('/login');
  };

  const bottomNavItems = [
    { text: 'داشبورد', icon: <DashboardIcon />, path: '/shop' },
    { text: 'سفارش جدید', icon: <AddIcon />, path: '/shop/orders/new' },
    { text: 'سفارشات', icon: <OrderIcon />, path: '/shop/orders' },
    { text: 'مشتریان', icon: <PeopleIcon />, path: '/shop/customers' },
  ];

  const getCurrentTab = () => {
    if (location.pathname.startsWith('/shop/orders/new')) return 1;
    if (location.pathname.startsWith('/shop/orders')) return 2;
    if (location.pathname.startsWith('/shop/customers')) return 3;
    return 0;
  };

  const menuItemStyle = (isSelected) => ({
    color: isSelected ? themeColors.gold : themeColors.text,
    '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.1)' },
    '&.Mui-selected': { backgroundColor: 'rgba(212, 175, 55, 0.15)' },
  });

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: themeColors.dark }}>
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: themeColors.darker, mt: '64px' }}>
        <Avatar
          sx={{
            width: 70,
            height: 70,
            mx: 'auto',
            mb: 1,
            bgcolor: themeColors.gold,
            border: `3px solid ${themeColors.goldLight}`,
          }}
        >
          <StoreIcon sx={{ fontSize: 35 }} />
        </Avatar>
        <Typography variant="h6" sx={{ color: themeColors.gold, fontWeight: 'bold' }}>
          {isSalesperson ? userInfo?.full_name || 'فروشنده' : userInfo?.shop_name || 'پرده فروشی'}
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.textMuted }}>
          {userInfo?.mobile || userInfo?.username}
        </Typography>
        
        <Box
          onClick={() => { navigate('/shop/profile'); setDrawerOpen(false); }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            cursor: 'pointer',
            color: themeColors.goldLight,
            '&:hover': { color: themeColors.gold },
          }}
        >
          <PersonIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">مشخصات من</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />

      <List sx={{ px: 1 }}>
        {/* داشبورد */}
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === '/shop'}
            onClick={() => { navigate('/shop'); setDrawerOpen(false); }}
            sx={menuItemStyle(location.pathname === '/shop')}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><DashboardIcon /></ListItemIcon>
            <ListItemText primary="داشبورد" />
          </ListItemButton>
        </ListItem>

        {/* سفارشات */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => setOrdersOpen(!ordersOpen)} sx={{ color: themeColors.text }}>
            <ListItemIcon sx={{ color: themeColors.gold, minWidth: 40 }}><OrderIcon /></ListItemIcon>
            <ListItemText primary="سفارشات" />
            {ordersOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={ordersOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pr: 4, ...menuItemStyle(location.pathname === '/shop/orders/new') }}
              selected={location.pathname === '/shop/orders/new'}
              onClick={() => { navigate('/shop/orders/new'); setDrawerOpen(false); }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><AddIcon /></ListItemIcon>
              <ListItemText primary="سفارش جدید" />
            </ListItemButton>
            <ListItemButton
              sx={{ pr: 4, ...menuItemStyle(location.pathname === '/shop/orders' && !location.pathname.includes('/new')) }}
              selected={location.pathname === '/shop/orders'}
              onClick={() => { navigate('/shop/orders'); setDrawerOpen(false); }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><ListIcon /></ListItemIcon>
              <ListItemText primary="لیست سفارشات" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* مشتریان */}
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === '/shop/customers'}
            onClick={() => { navigate('/shop/customers'); setDrawerOpen(false); }}
            sx={menuItemStyle(location.pathname === '/shop/customers')}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><PeopleIcon /></ListItemIcon>
            <ListItemText primary="مشتریان" />
          </ListItemButton>
        </ListItem>

        {/* فروشندگان - فقط صاحب فروشگاه */}
        {isShopOwner && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/shop/salespersons'}
              onClick={() => { navigate('/shop/salespersons'); setDrawerOpen(false); }}
              sx={menuItemStyle(location.pathname === '/shop/salespersons')}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><SalespersonIcon /></ListItemIcon>
              <ListItemText primary="فروشندگان" />
            </ListItemButton>
          </ListItem>
        )}

        {/* خیاط‌ها - فقط صاحب فروشگاه */}
        {isShopOwner && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/shop/tailors'}
              onClick={() => { navigate('/shop/tailors'); setDrawerOpen(false); }}
              sx={menuItemStyle(location.pathname === '/shop/tailors')}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><TailorIcon /></ListItemIcon>
              <ListItemText primary="خیاط‌ها" />
            </ListItemButton>
          </ListItem>
        )}

        {/* نصاب‌ها - فقط صاحب فروشگاه */}
        {isShopOwner && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/shop/installers'}
              onClick={() => { navigate('/shop/installers'); setDrawerOpen(false); }}
              sx={menuItemStyle(location.pathname === '/shop/installers')}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><InstallerIcon /></ListItemIcon>
              <ListItemText primary="نصاب‌ها" />
            </ListItemButton>
          </ListItem>
        )}

        {/* آمار فروش - فقط صاحب فروشگاه */}
        {isShopOwner && (
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/shop/stats'}
              onClick={() => { navigate('/shop/stats'); setDrawerOpen(false); }}
              sx={menuItemStyle(location.pathname === '/shop/stats')}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}><StatsIcon /></ListItemIcon>
              <ListItemText primary="آمار فروش" />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.2)' }} />
      
      <List sx={{ px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ color: '#ef5350' }}>
            <ListItemIcon sx={{ color: '#ef5350', minWidth: 40 }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="خروج" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: themeColors.dark,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {isMobile ? (
            <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Box />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isMobile && (
              <IconButton sx={{ color: themeColors.gold }} onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'bold',
                color: themeColors.gold,
                bgcolor: 'rgba(212, 175, 55, 0.15)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {currentTime}
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.textMuted, display: { xs: 'none', sm: 'block' } }}>
              {toPersianDate()}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': { 
              width: drawerWidth, 
              boxSizing: 'border-box', 
              border: 'none',
              bgcolor: themeColors.dark,
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          mb: { xs: '56px', md: 0 },
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
          <BottomNavigation
            value={getCurrentTab()}
            onChange={(e, newValue) => navigate(bottomNavItems[newValue].path)}
            showLabels
            sx={{
              bgcolor: themeColors.dark,
              '& .MuiBottomNavigationAction-root': { color: themeColors.textMuted },
              '& .Mui-selected': { color: `${themeColors.gold} !important` },
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction key={item.text} label={item.text} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default ShopLayout;